import { FC } from 'react';
import { Control, useController } from 'react-hook-form';
import styled, { css } from 'styled-components';

import { Label, Larger } from '@/components/Forms';
import ValidationMessage from '@/components/Forms/ValidationMessage';
import Text from '@/components/Text';

import { FormDataT } from './NewActionForm';

const days = [
  {
    value: 1,
    label: 'Monday',
  },
  {
    value: 2,
    label: 'Tuesday',
  },
  {
    value: 3,
    label: 'Wednesday',
  },
  {
    value: 4,
    label: 'Thursday',
  },
  {
    value: 5,
    label: 'Friday',
  },
  {
    value: 6,
    label: 'Saturday',
  },
  {
    value: 0,
    label: 'Sunday',
  },
];

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
  padding: 12px;
  border-radius: 4px;
  border: 1px solid ${({ theme }) => theme.colors.lightGray};
`;

const Day = styled(Text)<{ $active: boolean }>`
  height: 32px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid ${({ theme }) => theme.colors.lightGray};
  transition: 0.15s ease-out all;

  ${({ $active, theme }) =>
    $active &&
    css`
      background: ${theme.colors.purple};
      border-color: ${theme.colors.purple};
      color: ${theme.colors.white};
    `}
  ${({ $active, theme }) =>
    !$active &&
    css`
      &:hover {
        background: ${theme.colors.grayscaleLight};
      }
    `}
`;

interface Props {
  control: Control<FormDataT>;
}

const WeekdaysField: FC<Props> = ({ control }) => {
  const {
    field: { onChange, value },
    fieldState: { error },
  } = useController<FormDataT, 'weekdays'>({
    name: 'weekdays',
    control,
  });

  const onDayClick = (day: number) => {
    if (!value.includes(day)) {
      return onChange([...value, day]);
    }

    onChange(value.filter((v) => v !== day));
  };

  return (
    <Larger>
      <Label>Select Days:</Label>
      <Grid>
        {days.map((day) => (
          <Day
            type="button"
            as="button"
            $type="smallBold"
            $active={value.includes(day.value)}
            onClick={() => onDayClick(day.value)}
          >
            {day.label}
          </Day>
        ))}
      </Grid>
      {!!error && <ValidationMessage>{error.message}</ValidationMessage>}
    </Larger>
  );
};

export default WeekdaysField;
